import React, { useCallback } from 'react';
import type { ReactElement } from 'react';
import SpinnerContainer from './SpinnerContainer';
import TableMFHHeader from './TableMFHHeader';
import TableMFHRow from './TableMFHRow';
import { StyledTableHeaderStickyArea } from './StyledComponents';
import type { ColumnConfig } from './types';
import { ScrollTrigger } from './ScrollTrigger';
import { PinLeftOverlay } from './PinLeftOverlay';

const noop = () => {};

export interface AdvanvedTableGroupedRows<T> {
  groupRows: Array<T>;
  groupId: string;
}

export interface SortKeyType<T> {
  key?: keyof T;
  customKey?: string;
  ascending: boolean;
}

interface RowGroupingType<T> {
  indexToGroup: Record<string, string>;
  groupToRows: Record<string, Array<T>>;
  groupToIndex: Record<string, Array<number>>;
}

export enum AdvancedTableStyles {
  standard,
  stripes,
}

export type ExtendedRowData<T> = T & { absoluteRowNumber?: number };

interface AdvancedTableProps<T> {
  columnConfig: Array<ColumnConfig<T>>;
  data: Array<ExtendedRowData<T>>;
  defaultSortKey?: SortKeyType<T>;
  disableSelectAll?: boolean;
  edgePadding?: number;
  ErrorRowComponent?: () => JSX.Element;
  ExpandedComponent?: (rowData: T) => JSX.Element;
  groupingFunction?: (data: T) => string;
  groupingHeaderRenderer?: (
    group: AdvanvedTableGroupedRows<T>,
  ) => JSX.Element | string | undefined | ReactElement | number;
  handleLoadMoreData?: (isVisible: boolean) => void;
  hasMoreData?: boolean;
  isLoading?: boolean;
  isMobile?: boolean;
  isRowExpandable?: (rowData: T) => boolean;
  onSelectRow?: (newSelected: Set<number>, processedData: Array<T>) => void;
  rowSelectionDisabled?: (rowData: T) => boolean;
  selected?: Set<number> | ((data: Array<T>) => Set<number>);
  selectionDisabled?: boolean;
  stickyHeader?: boolean;
  topBannerComponent?: () => JSX.Element;
  tableStyle?: AdvancedTableStyles;
  minWeightWidth?: number;
  recyclerView?: boolean;
  fixedHeight?: number;
  enableGroupSelection?: boolean;
  rowPaddingY?: number;
  calculateRowHeight?: (rowData: T) => number;
}

export const AdvancedTable = <T,>({
  columnConfig = [],
  data = [],
  defaultSortKey = undefined,
  disableSelectAll = false,
  edgePadding = 0,
  ErrorRowComponent = () => <></>,
  ExpandedComponent,
  handleLoadMoreData = noop,
  hasMoreData = false,
  groupingFunction,
  groupingHeaderRenderer,
  isLoading = false,
  isMobile = false,
  selected = new Set(),
  onSelectRow = noop,
  topBannerComponent = () => <></>,
  selectionDisabled = false,
  rowSelectionDisabled = () => false,
  isRowExpandable = () => false,
  stickyHeader = true,
  tableStyle = AdvancedTableStyles.standard,
  minWeightWidth = 0,
  recyclerView = false,
  fixedHeight,
  calculateRowHeight,
  enableGroupSelection = true,
  rowPaddingY,
}: AdvancedTableProps<T>) => {
  const canSelectRow = onSelectRow !== noop;
  const showTopBanner = topBannerComponent !== noop;
  const showErrorRow = ErrorRowComponent !== noop;
  const hasExpandableRows: boolean = !!isRowExpandable && ExpandedComponent !== undefined;
  const mainTableRef = React.useRef<HTMLDivElement>(null);
  const [useFixedWidth, setUseFixedWidth] = React.useState<boolean>(false);
  const [offsetTopRows, setOffsetTopRows] = React.useState<number>(0);

  const checkWidth = useCallback(() => {
    if (!minWeightWidth) {
      setUseFixedWidth(false);
      return;
    }
    let useMinWeightPx = false;

    if (!mainTableRef.current) {
      return;
    }
    if (mainTableRef.current.clientWidth < minWeightWidth) {
      useMinWeightPx = true;
    } else {
      useMinWeightPx = false;
    }
    setUseFixedWidth(useMinWeightPx);
  }, [minWeightWidth]);

  React.useEffect(() => {
    checkWidth();
    window.addEventListener('resize', checkWidth);
    return () => window.removeEventListener('resize', checkWidth);
  }, [checkWidth]);

  let columnDivisions: number = columnConfig.reduce((val, col) => val + col.width, 0);
  if (canSelectRow) {
    columnDivisions += 1;
  }
  if (hasExpandableRows) {
    columnDivisions += 1;
  }

  const [sortKey, setSortKey] = React.useState<SortKeyType<T> | undefined>(defaultSortKey);
  const [expanded, setExpanded] = React.useState(new Set());

  const handleToggleExpanded = (i: number) => {
    const newExpanded = new Set(Array.from(expanded).filter((index) => index === i));
    if (newExpanded.has(i)) {
      newExpanded.delete(i);
    } else {
      newExpanded.add(i);
    }
    setExpanded(newExpanded);
  };

  let processedData: Array<ExtendedRowData<T>> = data;
  if (sortKey) {
    processedData = data.slice(0);
    const finalizedKey = sortKey?.key || sortKey?.customKey;
    const sortConfig = columnConfig.find((col) => {
      if (col.customKey) {
        return col.customKey === finalizedKey;
      } else if (col.dataKey) {
        return col.dataKey === finalizedKey;
      }
      return false;
    });
    processedData.sort((a, b) => {
      let aValue;
      let bValue;
      if (sortConfig?.sortValue) {
        aValue = sortConfig.sortValue(a);
        bValue = sortConfig.sortValue(b);
      } else if (sortKey.key) {
        aValue = a[sortKey.key];
        bValue = b[sortKey.key];
      } else {
        // eslint-disable-next-line no-console
        console.warn('When using customKey, sortValue must be provided');
      }
      if (aValue === bValue) return 0;
      if (sortKey.ascending) {
        if (aValue === undefined || aValue === null) return -1;
        if (bValue === undefined || bValue === null) return 1;
        if (typeof aValue === 'string') {
          return String(aValue).localeCompare(String(bValue), undefined, { numeric: true });
        }
        return aValue > bValue ? 1 : -1;
      }
      if (bValue === undefined || bValue === null) return -1;
      if (aValue === undefined || aValue === null) return 1;
      if (typeof aValue === 'string') {
        return String(bValue).localeCompare(String(aValue), undefined, { numeric: true });
      }
      return aValue < bValue ? 1 : -1;
    });
  }
  processedData = processedData.map((rowData, i) => ({
    ...(rowData as ExtendedRowData<T>),
    absoluteRowNumber: i,
  }));
  const selectedIndexes = typeof selected === 'function' ? selected(processedData) : selected;

  const canExpandCurrentRow = (rowData: T): boolean => {
    if (!hasExpandableRows || !isRowExpandable) {
      return false;
    }
    return isRowExpandable(rowData);
  };

  const groupingMap: RowGroupingType<T> = {
    indexToGroup: {},
    groupToRows: {},
    groupToIndex: {},
  };

  if (groupingFunction) {
    processedData.forEach((rowData, i) => {
      const groupId: string = groupingFunction(rowData);
      groupingMap.indexToGroup[i] = groupId;
      groupingMap.groupToRows[groupId] = groupingMap.groupToRows[groupId] || [];
      groupingMap.groupToRows[groupId].push(data[i]);
      groupingMap.groupToIndex[groupId] = groupingMap.groupToIndex[groupId] || [];
      groupingMap.groupToIndex[groupId].push(i);
    });
  }

  const handleSelectRow = (i: number) => {
    const newSelected = new Set(selectedIndexes);
    if (newSelected.has(i)) {
      newSelected.delete(i);
    } else {
      newSelected.add(i);
    }
    onSelectRow(newSelected, processedData);
  };

  const handleSelectAll = () => {
    const newSelected = new Set<number>();
    if (selectedIndexes.size === 0) {
      for (let i = 0; i < data.length; i++) {
        if (!rowSelectionDisabled(data[i])) {
          newSelected.add(i);
        }
      }
    }
    onSelectRow(newSelected, processedData);
  };

  const handleGroupToggle = (groupId: string) => {
    const newSelected = new Set(selectedIndexes);
    let selectedCount = 0;
    groupingMap.groupToIndex[groupId].forEach((i) => {
      if (newSelected.has(i)) selectedCount += 1;
    });
    groupingMap.groupToIndex[groupId].forEach((i) => {
      if (selectedCount === 0) {
        if (!rowSelectionDisabled(data[i])) {
          newSelected.add(i);
        }
      } else {
        newSelected.delete(i);
      }
    });
    onSelectRow(newSelected, processedData);
  };

  const isGroupSelected = (groupId: string) => {
    let isSelected = true;
    groupingMap.groupToIndex[groupId].forEach((i) => {
      isSelected = isSelected && selectedIndexes.has(i);
    });
    return isSelected;
  };

  const headerScrollRef = React.useRef<HTMLDivElement>(null);

  const handleScroll = (event: React.UIEvent<HTMLElement>) => {
    if (!headerScrollRef.current || headerScrollRef.current.scrollLeft == null) {
      return;
    }

    headerScrollRef.current.scrollLeft = event.currentTarget.scrollLeft;
  };

  const renderRows = (hideOverflow: boolean) => {
    let totalHeight = 0;
    let rowsInView = processedData;
    let calculatedRowHeights: Array<number> = [];
    const absoluteOffsets: Array<number> = [];
    if (recyclerView && fixedHeight) {
      if (!calculateRowHeight) {
        throw new Error('calculateRowHeight must be provided for recyclerView');
      }
      calculatedRowHeights = data.map((rowData) => calculateRowHeight(rowData));
      totalHeight = calculatedRowHeights.reduce((a, b) => a + b, 0);

      let startRowInView = -1;
      let endRowInView = -1;
      let currentHeight = 0;
      const maxPxVisible = offsetTopRows + fixedHeight;
      for (let i = 0; i < processedData.length; i++) {
        absoluteOffsets.push(currentHeight);
        if (currentHeight + calculatedRowHeights[i] >= offsetTopRows && startRowInView === -1) {
          startRowInView = i;
        }
        if (currentHeight + calculatedRowHeights[i] >= maxPxVisible && endRowInView === -1) {
          endRowInView = i + 1;
        }
        currentHeight += calculatedRowHeights[i];
      }
      if (endRowInView === -1) {
        endRowInView = calculatedRowHeights.length;
      }
      absoluteOffsets.push(calculatedRowHeights[calculatedRowHeights.length - 1]);
      rowsInView = processedData.slice(startRowInView, endRowInView);
    }
    return (
      <div
        style={{
          overflowY: hideOverflow ? 'auto' : undefined,
          paddingBottom: !hideOverflow ? 0 : '30px',
          height: totalHeight ? totalHeight : undefined,
          position: 'relative',
        }}
        onScroll={handleScroll}
      >
        {rowsInView.map((rowData, i) => (
          <TableMFHRow
            key={i}
            columnConfig={columnConfig}
            data={rowData}
            isSelected={selectedIndexes.has(i)}
            isExpanded={expanded.has(i)}
            canSelectRow={canSelectRow}
            canExpandCurrentRow={canExpandCurrentRow(rowData)}
            hasExpandableRows={hasExpandableRows}
            onToggleSelected={() => handleSelectRow(i)}
            onToggleExpanded={() => handleToggleExpanded(i)}
            ExpandedComponent={ExpandedComponent}
            columnDivisions={columnDivisions}
            selectionDisabled={selectionDisabled}
            rowSelectionDisabled={rowSelectionDisabled}
            edgePadding={edgePadding}
            rowNum={i}
            absoluteRowNumber={rowData.absoluteRowNumber}
            tableStyle={tableStyle}
            minWeightWidth={minWeightWidth}
            useFixedWidth={useFixedWidth}
            fixedWidth={minWeightWidth}
            topOffset={
              recyclerView && rowData.absoluteRowNumber ? absoluteOffsets[rowData.absoluteRowNumber] : undefined
            }
            rowPaddingY={rowPaddingY}
          />
        ))}
      </div>
    );
  };

  const isGroupDisabled = (currentGroup: string) =>
    groupingMap.groupToIndex[currentGroup].every((i) => rowSelectionDisabled(data[i]));

  const renderGroupedRows = () => {
    const rows: Array<React.ReactNode> = [];
    let currentGroup: string;
    processedData.forEach((rowData, i) => {
      if (groupingMap.indexToGroup[i] !== currentGroup) {
        if (!groupingHeaderRenderer) return '-';
        currentGroup = groupingMap.indexToGroup[i];
        const groupedRowData: AdvanvedTableGroupedRows<T> = {
          groupRows: groupingMap.groupToRows[currentGroup],
          groupId: currentGroup,
        };
        const rowColumnConfig: Array<ColumnConfig<AdvanvedTableGroupedRows<T>>> = [
          {
            width: enableGroupSelection ? columnDivisions - 2 : columnDivisions, // -1 for the select box column
            header: '',
            align: 'left',
            renderer: groupingHeaderRenderer,
          },
        ];
        rows.push(
          <TableMFHRow
            key={`group_header_${currentGroup}`}
            canSelectRow={canSelectRow && enableGroupSelection}
            data={groupedRowData}
            isSelected={isGroupSelected(currentGroup)}
            onToggleSelected={() => handleGroupToggle(groupingMap.indexToGroup[i])}
            columnConfig={rowColumnConfig}
            columnDivisions={columnDivisions}
            selectionDisabled={isGroupDisabled(currentGroup)}
            rowSelectionDisabled={() => false}
            hasExpandableRows={hasExpandableRows}
            edgePadding={edgePadding}
            canExpandCurrentRow={false}
            rowNum={i}
            tableStyle={AdvancedTableStyles.standard}
            minWeightWidth={minWeightWidth}
            useFixedWidth={useFixedWidth}
            fixedWidth={minWeightWidth}
            rowPaddingY={rowPaddingY}
          />,
        );
      }
      rows.push(
        <TableMFHRow
          key={i}
          columnConfig={columnConfig}
          data={rowData}
          isSelected={selectedIndexes.has(i)}
          isExpanded={expanded.has(i)}
          canSelectRow={canSelectRow}
          canExpandCurrentRow={isRowExpandable(rowData)}
          onToggleSelected={() => handleSelectRow(i)}
          onToggleExpanded={() => handleToggleExpanded(i)}
          ExpandedComponent={ExpandedComponent}
          columnDivisions={columnDivisions}
          selectionDisabled={selectionDisabled}
          rowSelectionDisabled={rowSelectionDisabled}
          hasExpandableRows={hasExpandableRows}
          edgePadding={edgePadding}
          rowNum={i}
          tableStyle={AdvancedTableStyles.standard}
          minWeightWidth={minWeightWidth}
          useFixedWidth={useFixedWidth}
          fixedWidth={minWeightWidth}
          rowPaddingY={rowPaddingY}
        />,
      );
    });
    return rows;
  };

  const handleSortClick = ({ key, customKey }: { key?: keyof T; customKey?: string }) => {
    if (!key && !customKey) {
      return setSortKey(undefined);
    }

    if (sortKey && ((sortKey.key && sortKey.key === key) || (sortKey.customKey && sortKey.customKey === customKey))) {
      setSortKey({
        key,
        customKey,
        ascending: !sortKey.ascending,
      });
    } else {
      setSortKey({
        key,
        customKey,
        ascending: true,
      });
    }
  };

  const renderTableHeader = (sticky: boolean) => (
    <StyledTableHeaderStickyArea sticky={sticky}>
      {showTopBanner && topBannerComponent()}
      <div style={{ overflowY: 'hidden', overflowX: 'hidden' }} ref={headerScrollRef}>
        <TableMFHHeader
          columnConfig={columnConfig}
          sortKey={sortKey}
          isSelected={selectedIndexes.size === processedData.length && processedData.length > 0}
          canSelectRow={canSelectRow}
          disableSelectAll={disableSelectAll}
          onSelectToggle={handleSelectAll}
          onSortClick={handleSortClick}
          isMobile={isMobile}
          columnDivisions={columnDivisions}
          selectionDisabled={selectionDisabled}
          hasExpandableRows={hasExpandableRows}
          edgePadding={edgePadding}
          tableStyle={tableStyle}
          useFixedWidth={useFixedWidth}
          minWeightWidth={minWeightWidth}
          fixedWidth={minWeightWidth}
        />
      </div>
    </StyledTableHeaderStickyArea>
  );

  const renderTableAfter = () => (
    <>
      {isLoading && <SpinnerContainer />}
      {showErrorRow && <ErrorRowComponent />}
      {hasMoreData && !isLoading && <ScrollTrigger onChange={handleLoadMoreData} />}
    </>
  );

  const renderTableContent = (hideOverflow: boolean) => (
    <>{groupingFunction ? renderGroupedRows() : renderRows(hideOverflow)}</>
  );

  const handleTableScroll = () => {
    if (!mainTableRef.current) {
      return;
    }
    setOffsetTopRows(mainTableRef.current.scrollTop || 0);
  };

  return (
    <div
      ref={mainTableRef}
      style={{
        position: 'relative',
        height: fixedHeight ? `${fixedHeight}px` : undefined,
        overflowX: fixedHeight ? 'hidden' : undefined,
      }}
      onScroll={handleTableScroll}
    >
      {useFixedWidth && !isLoading && (
        <PinLeftOverlay
          fixedWidth={minWeightWidth}
          columnDivisions={columnDivisions}
          columnConfig={columnConfig}
          content={renderTableContent(false)}
        />
      )}
      {renderTableHeader(stickyHeader)}
      {renderTableContent(true)}
      {renderTableAfter()}
    </div>
  );
};
